import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { memo, useCallback, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { useFormikContext } from 'formik';
import { ClaimsFormVM } from 'front-office/view-models/claims/claims-form';
import ClaimsCheckboxes from './form';
import IdentityCard from 'front-office/components/shared/identity-card';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';

const ClaimsAnprComponent = (): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(true);

  const formContext = useFormikContext<ClaimsFormVM>();

  const onChange = useCallback(
    (identifier) => {
      const isSelected =
        formContext.values.claims?.key.codiceDocumento === identifier.key.codiceDocumento;

      formContext.setFieldValue('claims', isSelected ? undefined : identifier);

      if (!formContext.values.certificateInternOnly && identifier.dettaglioModalitaRilascio) {
        formContext.setFieldValue(
          'modality',
          isSelected
            ? undefined
            : {
                id: identifier.dettaglioModalitaRilascio.key.codiceModalitaRilascio,
                value: identifier.dettaglioModalitaRilascio.descrizione,
                amountTaxStamp: identifier.dettaglioModalitaRilascio.importoImpostaBollo
                  ? Number.parseFloat(identifier.dettaglioModalitaRilascio.importoImpostaBollo)
                  : 0,
                amountRightsSecretariat: identifier.dettaglioModalitaRilascio
                  .importoDirittiSegreteria
                  ? Number.parseFloat(identifier.dettaglioModalitaRilascio.importoDirittiSegreteria)
                  : 0,
                amountRightsCivilState: identifier.dettaglioModalitaRilascio
                  .importoDirittiStatoCivile
                  ? Number.parseFloat(
                      identifier.dettaglioModalitaRilascio.importoDirittiStatoCivile
                    )
                  : 0,
                amountFixedRights: identifier.dettaglioModalitaRilascio.importoDirittiFissi
                  ? Number.parseFloat(identifier.dettaglioModalitaRilascio.importoDirittiFissi)
                  : 0,
                amountExpenses: identifier.dettaglioModalitaRilascio.importoSpese
                  ? Number.parseFloat(identifier.dettaglioModalitaRilascio.importoSpese)
                  : 0,
                codeExemptionAnpr: identifier.dettaglioModalitaRilascio.codiceEsenzioneAnpr,
                registryType: identifier.dettaglioModalitaRilascio.tipoRegistro,
                descrizioneModalitaStampa:
                  identifier.dettaglioModalitaRilascio.descrizioneModalitaStampa,
                disabled: true,
              }
        );
      }
    },
    [formContext.setFieldValue, formContext.values.claims]
  );

  const onFieldGeneralityChange = useCallback(
    (key, value) => {
      formContext.setFieldValue('generality', {
        ...formContext.values.generality,
        [key]: value,
      });
    },
    [formContext.values.generality, formContext.setFieldValue]
  );

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="panel-claims-anpr-content"
        id="panel-claims-anpr-header"
        expandIcon={<ExpandMore />}
      >
        <Typography>Attestazioni</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <ClaimsCheckboxes
            onChange={onChange}
            values={formContext.values.claims}
            isBritish={formContext.values.isBritish}
          />
          {formContext.values.claims ? (
            <Grid item xs={12} container spacing={2}>
              <IdentityCard
                onFieldGeneralityChange={onFieldGeneralityChange}
                generality={
                  formContext.values.accountholder?.account?.generality ||
                  formContext.values.generality
                }
              />
              {formContext.values.claims.key.codiceDocumento === '4' ? (
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Data richiesta"
                    aria-label="Data richiesta"
                    value={formContext.values.dateForm || null}
                    onChange={(date) => {
                      formContext.setFieldValue('dateForm', date);
                    }}
                    renderInput={(params) => (
                      <PmiTextField
                        {...params}
                        id="dateForm"
                        fullWidth
                        helperText={formContext.errors.dateForm}
                        error={formContext.touched.dateForm && Boolean(formContext.errors.dateForm)}
                      />
                    )}
                  />
                </Grid>
              ) : undefined}
              {formContext.values.claims.key.codiceDocumento === '2' ||
              formContext.values.claims.key.codiceDocumento === '4' ? (
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="family-citizen-ue"
                        checked={!!formContext.values.citizenUE}
                        onChange={() =>
                          formContext.setFieldValue('citizenUE', !formContext.values.citizenUE)
                        }
                        inputProps={{ 'aria-label': 'Familiare cittadino iscritto' }}
                      />
                    }
                    label="Familiare Cittadino iscritto"
                  />
                </Grid>
              ) : undefined}
            </Grid>
          ) : undefined}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

ClaimsAnprComponent.displayName = 'Accordion claims anpr component';

const ClaimsAnpr = memo(ClaimsAnprComponent);

export default ClaimsAnpr;
